import { Classes } from "@blueprintjs/core";
import { ItemRenderer, Select } from "@blueprintjs/select";
import {
  DatetimeValue,
  RelativeDateOperation,
  RelativeDateUnit,
  RelativeDatetimeOffsetValue,
} from "@hex/common";
import moment from "moment-timezone";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuDivider,
  HexMenuItem,
  HexNumericInput,
  HexTimezonePicker,
  TimezoneDisplayFormat,
  Txt,
} from "../../hex-components";
import { SingleChevronDownIcon, TickIcon } from "../icons/CustomIcons";

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
const GUESSED_TIMEZONE = moment.tz.guess();

const CustomRelativeDateDiv = styled.div<{ $enabled?: boolean }>`
  display: flex;
  gap: 5px;
  padding-top: 5px;
  align-items: center;

  ${({ $enabled }) =>
    $enabled
      ? `
    pointer-events: all;
    opacity: 1;
  `
      : `
    pointer-events: none;
    opacity: 0.5;
  `}
`;

const StyledNumericInput = styled(HexNumericInput)`
  /* 1px offset to adjust for negative margins from BP */
  padding-right: 1px;
`;

const RelativeDatePickerContainerDiv = styled.div`
  display: flex;
  justify-content: space-around;
  padding-top: 5px;
`;

const RelativeDatesDescription = styled.div`
  padding: 0 6px 4px;

  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

const MenuControl = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 200px;
  padding: 2px 10px;
`;

const CustomOptionButton = styled(HexButton)`
  &&&& {
    padding-right: 5px;

    .${Classes.BUTTON_TEXT}:not(:last-child) {
      margin-right: 4px;
    }
  }
`;

type RelativeDateShortcut = {
  displayName: string;
  offset?: RelativeDatetimeOffsetValue;
};

export const getUnitDisplayName = (
  value: RelativeDatetimeOffsetValue,
  unit?: RelativeDateUnit,
): string => {
  if (unit) {
    return value.amount !== 1
      ? `${relativeDateUnitDisplayNames[unit].displayNamePlural}`
      : `${relativeDateUnitDisplayNames[unit].displayName}`;
  } else {
    return value.amount !== 1
      ? `${relativeDateUnitDisplayNames[value.unit].displayNamePlural}`
      : `${relativeDateUnitDisplayNames[value.unit].displayName}`;
  }
};

export const relativeDatetimeOffsetToDisplayString = (
  value: RelativeDatetimeOffsetValue,
  useDisplayName = true,
): string => {
  if (value.displayName && useDisplayName) {
    return value.displayName;
  } else {
    return `${value.amount} ${getUnitDisplayName(value)} ${
      relativeDateOperationDisplayNames[value.operation].displayName
    }`;
  }
};

const relativeDateShortcuts: RelativeDateShortcut[] = [
  {
    displayName: "Today",
    offset: {
      unit: RelativeDateUnit.DAY,
      amount: 0,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Yesterday",
    offset: {
      unit: RelativeDateUnit.DAY,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "1 week ago",
    offset: {
      unit: RelativeDateUnit.WEEK,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "1 month ago",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "3 months ago",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 3,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "1 year ago",
    offset: {
      unit: RelativeDateUnit.YEAR,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  { displayName: "Custom" },
];

const relativeDateShortcutExtensions: RelativeDateShortcut[] = [
  {
    displayName: "This year",
    offset: {
      unit: RelativeDateUnit.YEAR,
      amount: 0,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "This quarter",
    offset: {
      unit: RelativeDateUnit.QUARTER,
      amount: 0,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "This month",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 0,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "This week",
    offset: {
      unit: RelativeDateUnit.WEEK,
      amount: 0,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
];

export const relativeDateRangeShortcuts: RelativeDateShortcut[] = [
  {
    displayName: "Past week",
    offset: {
      unit: RelativeDateUnit.WEEK,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past month",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past 3 months",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 3,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past 6 months",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 6,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past year",
    offset: {
      unit: RelativeDateUnit.YEAR,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past 2 years",
    offset: {
      unit: RelativeDateUnit.YEAR,
      amount: 2,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  { displayName: "Custom" },
];

export const relativeDateRangeShortcutsExtended: RelativeDateShortcut[] = [
  {
    displayName: "Past week",
    offset: {
      unit: RelativeDateUnit.WEEK,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past month",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past quarter",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 3,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past 6 months",
    offset: {
      unit: RelativeDateUnit.MONTH,
      amount: 6,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past year",
    offset: {
      unit: RelativeDateUnit.YEAR,
      amount: 1,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  {
    displayName: "Past 2 years",
    offset: {
      unit: RelativeDateUnit.YEAR,
      amount: 2,
      operation: RelativeDateOperation.AGO,
      timezone: GUESSED_TIMEZONE,
    },
  },
  { displayName: "Custom" },
];

export const relativeDateUnitDisplayNames = {
  [RelativeDateUnit.DAY]: {
    displayName: "day",
    displayNamePlural: "days",
  },
  [RelativeDateUnit.WEEK]: {
    displayName: "week",
    displayNamePlural: "weeks",
  },
  [RelativeDateUnit.MONTH]: {
    displayName: "month",
    displayNamePlural: "months",
  },
  [RelativeDateUnit.QUARTER]: {
    displayName: "quarter",
    displayNamePlural: "quarters",
  },
  [RelativeDateUnit.YEAR]: {
    displayName: "year",
    displayNamePlural: "years",
  },
};

export const relativeDateOperationDisplayNames = {
  [RelativeDateOperation.AGO]: {
    displayName: "ago",
  },
  [RelativeDateOperation.FROM_NOW]: {
    displayName: "from now",
  },
};

const defaultRelativeDateValue: RelativeDatetimeOffsetValue = {
  unit: RelativeDateUnit.DAY,
  amount: 1,
  operation: RelativeDateOperation.AGO,
  timezone: GUESSED_TIMEZONE,
};

interface UseRelativeDatetimeOffsetChangeHandlerArgs<
  K extends keyof RelativeDatetimeOffsetValue,
> {
  field: K;
  currentValue?: DatetimeValue;
  setCurrentValue: (value: DatetimeValue) => void;
  onChangeValue?: (value: DatetimeValue) => void;
}
function useRelativeDatetimeOffsetChangeHandler<
  K extends keyof RelativeDatetimeOffsetValue,
>({
  currentValue,
  field,
  onChangeValue,
  setCurrentValue,
}: UseRelativeDatetimeOffsetChangeHandlerArgs<K>): (
  value: RelativeDatetimeOffsetValue[K],
) => void {
  return useCallback(
    (value: RelativeDatetimeOffsetValue[K]) => {
      const baseValue = RelativeDatetimeOffsetValue.guard(currentValue)
        ? currentValue
        : defaultRelativeDateValue;
      const newValue = {
        ...baseValue,
        [field]: value,
      };
      setCurrentValue(newValue);
      onChangeValue?.(newValue);
    },
    [field, currentValue, setCurrentValue, onChangeValue],
  );
}
interface RelativeDatePickerProps {
  currentValue?: DatetimeValue;
  setCurrentValue: (newValue: DatetimeValue | undefined) => void;
  onChangeValue?: (newValue: DatetimeValue | undefined) => void;
  enableRelativeDatePicker: boolean;
  enableTimezonePicker: boolean;
  enableExtendedRelativeDateShortcuts?: boolean; // adds this year/month/week, supported in hex-sl only
  isDateRange?: boolean;
}

export const RelativeDatePicker: React.ComponentType<RelativeDatePickerProps> =
  React.memo(function RelativeDatePickerWrapper({
    currentValue,
    enableExtendedRelativeDateShortcuts,
    enableRelativeDatePicker,
    enableTimezonePicker,
    isDateRange,
    onChangeValue,
    setCurrentValue,
  }): JSX.Element {
    const [selectedShortcut, setSelectedShortcut] =
      useState<RelativeDateShortcut>();

    const onClickRelativeDateShortcut = useCallback(
      (shortcut: RelativeDateShortcut) => {
        const tz =
          RelativeDatetimeOffsetValue.guard(currentValue) &&
          currentValue.timezone
            ? currentValue.timezone
            : defaultRelativeDateValue.timezone;
        if (shortcut.displayName === "Custom") {
          if (
            RelativeDatetimeOffsetValue.guard(currentValue) &&
            customRelativeOptionSelected(currentValue, selectedShortcut)
          ) {
            return;
          } else {
            const newValue = {
              ...defaultRelativeDateValue,
              timezone: tz,
            };
            setCurrentValue(newValue);
            setSelectedShortcut(shortcut);
            onChangeValue?.(newValue);
          }
        } else if (shortcut.offset) {
          const newValue = {
            amount: shortcut.offset.amount,
            unit: shortcut.offset.unit,
            operation: shortcut.offset.operation,
            displayName: shortcut.displayName,
            timezone: tz,
          };
          setCurrentValue(newValue);
          setSelectedShortcut(shortcut);
          onChangeValue?.(newValue);
        } else {
          console.error("Invalid shortcut selected");
        }
      },

      [currentValue, selectedShortcut, setCurrentValue, onChangeValue],
    );

    const onChangeRelativeDateAmount = useRelativeDatetimeOffsetChangeHandler({
      field: "amount",
      currentValue,
      setCurrentValue,
      onChangeValue,
    });

    const onChangeRelativeDateUnit = useRelativeDatetimeOffsetChangeHandler({
      field: "unit",
      currentValue,
      setCurrentValue,
      onChangeValue,
    });

    const onChangeRelativeDateOperation =
      useRelativeDatetimeOffsetChangeHandler({
        field: "operation",
        currentValue,
        setCurrentValue,
        onChangeValue,
      });

    const onChangeRelativeDateTimezone = useCallback(
      (timezone: string) => {
        if (RelativeDatetimeOffsetValue.guard(currentValue)) {
          const newValue = {
            ...currentValue,
            timezone,
          };
          setCurrentValue(newValue);
          onChangeValue?.(newValue);
        } else {
          const newValue = {
            ...defaultRelativeDateValue,
            timezone,
          };
          setCurrentValue(newValue);
          onChangeValue?.(newValue);
        }
      },
      [currentValue, setCurrentValue, onChangeValue],
    );

    const relativeTimezone = useMemo(() => {
      if (currentValue && RelativeDatetimeOffsetValue.guard(currentValue)) {
        return currentValue.timezone;
      } else {
        return GUESSED_TIMEZONE;
      }
    }, [currentValue]);

    const customRelativeOptionSelected = (
      currValue?: RelativeDatetimeOffsetValue,
      currShortcut?: RelativeDateShortcut,
    ): boolean => {
      return !currValue?.displayName || currShortcut?.displayName === "Custom";
    };

    const renderRelativeDateUnit: ItemRenderer<RelativeDateUnit> = useCallback(
      (item, { handleClick }) => {
        return (
          <HexMenuItem
            key={item}
            shouldDismissPopover={false}
            text={
              RelativeDatetimeOffsetValue.guard(currentValue) &&
              currentValue.amount !== 1
                ? `${relativeDateUnitDisplayNames[item].displayNamePlural}`
                : `${relativeDateUnitDisplayNames[item].displayName}`
            }
            onClick={handleClick}
          />
        );
      },
      [currentValue],
    );

    const renderRelativeDateOperation: ItemRenderer<RelativeDateOperation> =
      useCallback((item, { handleClick }) => {
        return (
          <HexMenuItem
            key={item}
            shouldDismissPopover={false}
            text={`${relativeDateOperationDisplayNames[item].displayName}`}
            onClick={handleClick}
          />
        );
      }, []);

    const customOptionElement = (
      <CustomRelativeDateDiv
        $enabled={
          RelativeDatetimeOffsetValue.guard(currentValue) &&
          customRelativeOptionSelected(currentValue, selectedShortcut)
        }
        // eslint-disable-next-line react/jsx-no-bind
        onClick={(evt) => evt.stopPropagation()}
      >
        {isDateRange && <Txt>Past&nbsp;</Txt>}
        <StyledNumericInput
          $inputWidth="33px"
          $small={true}
          min={0}
          minorStepSize={1}
          value={
            RelativeDatetimeOffsetValue.guard(currentValue) &&
            customRelativeOptionSelected(currentValue, selectedShortcut)
              ? currentValue.amount
              : defaultRelativeDateValue.amount
          }
          onValueChange={onChangeRelativeDateAmount}
        />
        <Select
          filterable={false}
          itemRenderer={renderRelativeDateUnit}
          items={Object.values(RelativeDateUnit)}
          popoverProps={{ minimal: true }}
          resetOnClose={true}
          onItemSelect={onChangeRelativeDateUnit}
        >
          <CustomOptionButton
            rightIcon={<SingleChevronDownIcon iconSize={12} />}
            small={true}
            text={
              RelativeDatetimeOffsetValue.guard(currentValue) &&
              customRelativeOptionSelected(currentValue, selectedShortcut)
                ? getUnitDisplayName(currentValue)
                : getUnitDisplayName(defaultRelativeDateValue)
            }
          />
        </Select>
        {!isDateRange && (
          <Select
            filterable={false}
            itemRenderer={renderRelativeDateOperation}
            items={Object.values(RelativeDateOperation)}
            popoverProps={{ minimal: true }}
            resetOnClose={true}
            onItemSelect={onChangeRelativeDateOperation}
          >
            <CustomOptionButton
              rightIcon={<SingleChevronDownIcon iconSize={12} />}
              small={true}
              text={
                RelativeDatetimeOffsetValue.guard(currentValue) &&
                customRelativeOptionSelected(currentValue, selectedShortcut)
                  ? `${
                      relativeDateOperationDisplayNames[currentValue.operation]
                        .displayName
                    }`
                  : `${
                      relativeDateOperationDisplayNames[
                        defaultRelativeDateValue.operation
                      ].displayName
                    }`
              }
            />
          </Select>
        )}
      </CustomRelativeDateDiv>
    );

    const relativeDateMenuItems = (
      isDateRange
        ? enableExtendedRelativeDateShortcuts
          ? relativeDateRangeShortcutsExtended
          : relativeDateRangeShortcuts
        : enableExtendedRelativeDateShortcuts
          ? [...relativeDateShortcutExtensions, ...relativeDateShortcuts]
          : relativeDateShortcuts
    ).map((shortcut) => {
      const isActive =
        (RelativeDatetimeOffsetValue.guard(currentValue) &&
          currentValue?.displayName === shortcut.displayName) ||
        (RelativeDatetimeOffsetValue.guard(currentValue) &&
          !currentValue?.displayName &&
          shortcut.displayName === "Custom");
      const isCustomOption =
        enableRelativeDatePicker && shortcut.displayName === "Custom";
      return (
        <HexMenuItem
          key={shortcut.displayName}
          $align={isCustomOption ? "start" : "center"}
          active={isActive}
          labelElement={isActive ? <TickIcon /> : null}
          text={
            !isCustomOption ? (
              shortcut.displayName
            ) : (
              <>
                {shortcut.displayName}
                {customOptionElement}
              </>
            )
          }
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => {
            onClickRelativeDateShortcut(shortcut);
          }}
        />
      );
    });

    return (
      <RelativeDatePickerContainerDiv>
        <HexMenu>
          <RelativeDatesDescription>
            {isDateRange ? "Day is in the..." : "Dates are relative to today"}
          </RelativeDatesDescription>
          {relativeDateMenuItems}
          {enableTimezonePicker && (
            <>
              <HexMenuDivider title="Settings" />
              <MenuControl className={Classes.POPOVER_DISMISS_OVERRIDE}>
                Timezone
                <HexTimezonePicker
                  value={relativeTimezone}
                  valueDisplayFormat={TimezoneDisplayFormat.ABBREVIATION}
                  onChange={onChangeRelativeDateTimezone}
                />
              </MenuControl>
            </>
          )}
        </HexMenu>
      </RelativeDatePickerContainerDiv>
    );
  });
